import React, { useEffect, useRef, useState } from 'react';
import { tenantApi } from '../lib/tenant-api';
import { useTenantAuth } from '../contexts/TenantAuthContext';

interface WidgetAutoLoaderProps {
  enabled?: boolean;
  apiKey?: string | null;
  showErrorNotification?: boolean;
}

declare global {
  interface Window {
    LAWMAai?: {
      init: (config: any) => void;
      destroy?: () => void;
      updateConfig?: (partialConfig: any) => void;
      clearChat?: () => void;
      renderTo?: (markdown: string, targetEl?: HTMLElement, options?: Record<string, unknown>) => void;
    };
  }
}

export const WidgetAutoLoader: React.FC<WidgetAutoLoaderProps> = ({
  enabled = true,
  apiKey,
  showErrorNotification = false,
}) => {
  const { isAuthenticated, tenant } = useTenantAuth();
  const [error, setError] = useState<string | null>(null);
  const scriptLoadedRef = useRef(false);
  const widgetInitializedRef = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // ── Script loader ──────────────────────────────────────────────────────────
  const loadWidgetScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (scriptLoadedRef.current) { resolve(); return; }

      const existingScript = document.querySelector('script[src*="widget.js"]');
      if (existingScript) {
        // Script tag already in DOM — wait for it to fire load if it hasn't yet
        if ((existingScript as HTMLScriptElement).dataset.loaded === 'true') {
          scriptLoadedRef.current = true;
          resolve();
          return;
        }
        existingScript.addEventListener('load', () => { scriptLoadedRef.current = true; resolve(); }, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load widget script')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src =
        import.meta.env.VITE_WIDGET_SCRIPT_URL ||
        'https://storelawmaprodwestus2001.z5.web.core.windows.net/widget.js';
      script.async = true;
      script.onload = () => {
        script.dataset.loaded = 'true';
        scriptLoadedRef.current = true;
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load widget script'));
      document.head.appendChild(script);
    });
  };

  // ── API key resolver ───────────────────────────────────────────────────────
  const getPreviewApiKey = async (): Promise<string | null> => {
    if (apiKey) return apiKey;
    try {
      const preview = await tenantApi.getPreviewApiKey();
      return preview?.plain_key ?? null;
    } catch {
      return null;
    }
  };

  // ── Init / cleanup ─────────────────────────────────────────────────────────
  const initializeWidget = async (resolvedKey: string) => {
    await loadWidgetScript();
    if (!enabledRef.current) return;

    const config = {
      apiKey: resolvedKey,
      enableFeedbackCollection: true,
      // logoUrl:
      //   import.meta.env.VITE_WIDGET_LOGO_URL ||
      //   'https://storelawmaprodwestus2001.z5.web.core.windows.net/images/lawma/lawma.png',
    };

    if (window.LAWMAai && typeof window.LAWMAai.init === 'function') {
      try {
        window.LAWMAai.init(config);
        widgetInitializedRef.current = true;
        return;
      } catch (initError) {
        const message = initError instanceof Error ? initError.message : '';
        if (!message.includes('main initializer')) throw initError;
      }
    }

    if (window.LAWMAai && typeof window.LAWMAai.updateConfig === 'function') {
      window.LAWMAai.updateConfig(config);
      widgetInitializedRef.current = true;
      return;
    }

    console.warn('Widget init is handled by the main initializer; skipping local init.');
  };

  const cleanupWidget = (fullReset = false) => {
    if (window.LAWMAai && typeof window.LAWMAai.destroy === 'function') {
      try { window.LAWMAai.destroy(); } catch { /* ignore */ }
    }
    widgetInitializedRef.current = false;

    document.querySelectorAll('[id*="lawma"], [class*="lawma"]').forEach(el => el.remove());

    if (fullReset) {
      const scriptEl = document.querySelector('script[src*="widget.js"]');
      if (scriptEl) scriptEl.remove();
      scriptLoadedRef.current = false;
      window.LAWMAai = undefined;
    }
  };

  // ── Main load ──────────────────────────────────────────────────────────────
  const loadWidget = async () => {
    if (!enabled || !isAuthenticated || !tenant) return;

    try {
      setError(null);
      const resolvedKey = await getPreviewApiKey();
      if (!enabledRef.current) return;

      if (!resolvedKey) {
        setError('No API key available for widget preview');
        return;
      }

      await initializeWidget(resolvedKey);
    } catch (err) {
      console.error('Failed to load widget:', err);
      setError('Failed to load widget preview');
    }
  };

  // Pre-load script immediately on mount so it's ready before the key fetch completes
  useEffect(() => {
    if (enabled) {
      loadWidgetScript().catch(() => { /* will retry in loadWidget */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Full reset on unmount — removes the script tag and clears window.LAWMAai so
  // that the next mount (triggered by a key change on login/logout) always reloads
  // the script fresh. Without this, destroy() may have nullified window.LAWMAai
  // while the stale <script data-loaded="true"> tag prevents a reload, causing
  // init() to be silently skipped.
  useEffect(() => {
    return () => cleanupWidget(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Main effect — init when auth is ready; re-init on config change events
  useEffect(() => {
    if (enabled && isAuthenticated && tenant && !widgetInitializedRef.current) {
      loadWidget();
    } else if (!enabled || !isAuthenticated || !tenant) {
      cleanupWidget();
    }

    const handleApiKeyChanged = () => {
      cleanupWidget();
      loadWidget();
    };

    const handleConfigChanged = () => {
      cleanupWidget(true);
      loadWidget();
    };

    // Called when the user logs in without a page reload (deferred via setTimeout
    // so this fires after React has re-rendered and updated the closure).
    // Only loads if the main-effect path hasn't already initialised the widget.
    const handleUserLoggedIn = () => {
      if (!widgetInitializedRef.current) {
        loadWidget();
      }
    };

    window.addEventListener('widget-api-key-changed', handleApiKeyChanged);
    window.addEventListener('widget-config-changed', handleConfigChanged);
    window.addEventListener('user-logged-in', handleUserLoggedIn);

    return () => {
      window.removeEventListener('widget-api-key-changed', handleApiKeyChanged);
      window.removeEventListener('widget-config-changed', handleConfigChanged);
      window.removeEventListener('user-logged-in', handleUserLoggedIn);
      cleanupWidget();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isAuthenticated, tenant, apiKey]);

  // Error notice
  if (error && enabled && isAuthenticated && showErrorNotification) {
    return (
      <div className="fixed bottom-4 right-4 bg-yellow-50 border border-yellow-200 p-3 shadow-lg max-w-sm z-50">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-2">
            <h4 className="text-sm font-medium text-yellow-800">Widget Preview Unavailable</h4>
            <p className="text-xs text-yellow-700 mt-1">{error}</p>
            {error.includes('No API key') && (
              <p className="text-xs text-yellow-600 mt-1">
                Create an API key marked as preview key on the API Keys page.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default WidgetAutoLoader;
